import {
    HoldingsReportingOverrides,
    PerformanceReportingOverrides,
    ReportingChannelVisibility,
    ReportingChannelVisibilityOverride,
    ReportingPeriod,
    ReportingPreset,
    ReportingSettings,
    ReportingSettingsOverrides,
    RiskStatistic,
} from '@ndvr/reporting-api';

export type ReportingChannel = keyof ReportingChannelVisibility;
export type ReportingSettingId =
    | 'advisoryFeesPaid'
    | 'trailingReturns'
    | 'benchmarkComparison'
    | 'riskStatistics'
    | 'accruedBondIncome'
    | 'bondAnalytics'
    | 'granularAssetClassBreakdown';

export const REPORTING_CHANNELS: { key: ReportingChannel; label: string }[] = [
    { key: 'portalVisible', label: 'Portal' },
    { key: 'hubVisible', label: 'Hub' },
    { key: 'reportsVisible', label: 'Reports' },
];

export const REPORTING_PRESETS = [ReportingPreset.SIMPLIFIED, ReportingPreset.STANDARD, ReportingPreset.FULL_DETAIL];

export const REPORTING_PRESET_LABELS: Record<ReportingPreset, string> = {
    [ReportingPreset.SIMPLIFIED]: 'Simplified',
    [ReportingPreset.STANDARD]: 'Standard',
    [ReportingPreset.FULL_DETAIL]: 'Full Detail',
};

export const REPORTING_PERIOD_LABELS: Record<ReportingPeriod, string> = {
    [ReportingPeriod.QTD]: 'QTD',
    [ReportingPeriod.YTD]: 'YTD',
    [ReportingPeriod.ONE_YEAR]: '1 year',
    [ReportingPeriod.THREE_YEAR]: '3 years',
    [ReportingPeriod.FIVE_YEAR]: '5 years',
    [ReportingPeriod.TEN_YEAR]: '10 years',
    [ReportingPeriod.INCEPTION_TO_DATE]: 'Inception to date',
};

export const RISK_STATISTIC_LABELS: Record<RiskStatistic, string> = {
    [RiskStatistic.ALPHA]: 'Alpha',
    [RiskStatistic.BETA]: 'Beta',
    [RiskStatistic.R_SQUARED]: 'R-squared',
    [RiskStatistic.SHARPE_RATIO]: 'Sharpe ratio',
    [RiskStatistic.SORTINO_RATIO]: 'Sortino ratio',
    [RiskStatistic.STANDARD_DEVIATION]: 'Standard deviation',
    [RiskStatistic.DOWNSIDE_DEVIATION]: 'Downside deviation',
};

export type ReportingSettingDefinition = {
    id: ReportingSettingId;
    label: string;
    description?: string;
    selection?: 'periods' | 'statistics';
};

export const ADVISORY_FEES_SETTING: ReportingSettingDefinition = {
    id: 'advisoryFeesPaid',
    label: 'Advisory Fees Paid',
    description: 'Show in cash flow summaries',
};

export const REPORTING_SETTING_GROUPS: {
    id: string;
    title: string;
    description: string;
    settings: ReportingSettingDefinition[];
}[] = [
    {
        id: 'performance',
        title: 'Performance',
        description: 'Returns, benchmarks, and risk analytics',
        settings: [
            {
                id: 'trailingReturns',
                label: 'Trailing Returns table (QTD / YTD / 1yr / 3yr / 5yr / 10yr / ITD)',
                selection: 'periods',
            },
            {
                id: 'benchmarkComparison',
                label: 'Benchmark Comparison',
                description: 'Benchmark: Blended (matches portfolio allocation)',
            },
            {
                id: 'riskStatistics',
                label: 'Risk Statistics (Sharpe, Beta, Alpha, and related measures)',
                selection: 'statistics',
            },
        ],
    },
    {
        id: 'holdings',
        title: 'Holdings',
        description: 'Position detail, income, and asset-class breakdowns',
        settings: [
            { id: 'accruedBondIncome', label: 'Accrued Income on Bond Positions' },
            {
                id: 'bondAnalytics',
                label: 'Bond Analytics (Yield to Maturity, Duration, Maturity Ladder)',
                description: 'Manually enabled — does not turn on automatically based on bond holdings.',
            },
            { id: 'granularAssetClassBreakdown', label: 'Granular Asset-Class Breakdown' },
        ],
    },
];

const applyVisibilityOverride = (
    base: ReportingChannelVisibility,
    override?: ReportingChannelVisibilityOverride,
): ReportingChannelVisibility => ({
    portalVisible: override?.portalVisible ?? base.portalVisible,
    reportsVisible: override?.reportsVisible ?? base.reportsVisible,
    hubVisible: override?.hubVisible ?? base.hubVisible,
});

export const applyReportingSettingsOverrides = (
    base: ReportingSettings,
    overrides: ReportingSettingsOverrides,
): ReportingSettings => ({
    advisoryFeesPaid: applyVisibilityOverride(base.advisoryFeesPaid, overrides.advisoryFeesPaid),
    performance: {
        trailingReturns: {
            visibility: applyVisibilityOverride(
                base.performance.trailingReturns.visibility,
                overrides.performance?.trailingReturns?.visibility,
            ),
            enabledPeriods:
                overrides.performance?.trailingReturns?.enabledPeriods ??
                base.performance.trailingReturns.enabledPeriods,
        },
        benchmarkComparison: applyVisibilityOverride(
            base.performance.benchmarkComparison,
            overrides.performance?.benchmarkComparison,
        ),
        riskStatistics: {
            visibility: applyVisibilityOverride(
                base.performance.riskStatistics.visibility,
                overrides.performance?.riskStatistics?.visibility,
            ),
            statistics: overrides.performance?.riskStatistics?.statistics ?? base.performance.riskStatistics.statistics,
        },
    },
    holdings: {
        accruedBondIncome: applyVisibilityOverride(
            base.holdings.accruedBondIncome,
            overrides.holdings?.accruedBondIncome,
        ),
        bondAnalytics: applyVisibilityOverride(base.holdings.bondAnalytics, overrides.holdings?.bondAnalytics),
        granularAssetClassBreakdown: applyVisibilityOverride(
            base.holdings.granularAssetClassBreakdown,
            overrides.holdings?.granularAssetClassBreakdown,
        ),
    },
});

// Every per-setting path (read effective visibility, read/write/clear its override,
// detect an override) lives in exactly one lens entry, so the accessors cannot drift
// apart the way parallel switch statements could.
type VisibilityLens = {
    read: (settings: ReportingSettings) => ReportingChannelVisibility;
    readOverride: (overrides: ReportingSettingsOverrides) => ReportingChannelVisibilityOverride | undefined;
    writeOverride: (
        overrides: ReportingSettingsOverrides,
        visibility: ReportingChannelVisibilityOverride,
    ) => ReportingSettingsOverrides;
    isOverridden: (overrides: ReportingSettingsOverrides) => boolean;
    clearOverride: (overrides: ReportingSettingsOverrides) => ReportingSettingsOverrides;
};

const withoutPerformanceKey = (
    overrides: ReportingSettingsOverrides,
    key: keyof PerformanceReportingOverrides,
): ReportingSettingsOverrides => {
    const performance = { ...overrides.performance };
    delete performance[key];
    const next = { ...overrides };
    if (Object.keys(performance).length === 0) {
        delete next.performance;
    } else {
        next.performance = performance;
    }
    return next;
};

const withoutHoldingsKey = (
    overrides: ReportingSettingsOverrides,
    key: keyof HoldingsReportingOverrides,
): ReportingSettingsOverrides => {
    const holdings = { ...overrides.holdings };
    delete holdings[key];
    const next = { ...overrides };
    if (Object.keys(holdings).length === 0) {
        delete next.holdings;
    } else {
        next.holdings = holdings;
    }
    return next;
};

const holdingsVisibilityLens = (key: keyof HoldingsReportingOverrides): VisibilityLens => ({
    read: (settings) => settings.holdings[key],
    readOverride: (overrides) => overrides.holdings?.[key],
    writeOverride: (overrides, visibility) => ({
        ...overrides,
        holdings: { ...overrides.holdings, [key]: visibility },
    }),
    isOverridden: (overrides) => overrides.holdings?.[key] !== undefined,
    clearOverride: (overrides) => withoutHoldingsKey(overrides, key),
});

const VISIBILITY_LENSES: Record<ReportingSettingId, VisibilityLens> = {
    advisoryFeesPaid: {
        read: (settings) => settings.advisoryFeesPaid,
        readOverride: (overrides) => overrides.advisoryFeesPaid,
        writeOverride: (overrides, visibility) => ({ ...overrides, advisoryFeesPaid: visibility }),
        isOverridden: (overrides) => overrides.advisoryFeesPaid !== undefined,
        clearOverride: (overrides) => {
            const next = { ...overrides };
            delete next.advisoryFeesPaid;
            return next;
        },
    },
    trailingReturns: {
        read: (settings) => settings.performance.trailingReturns.visibility,
        readOverride: (overrides) => overrides.performance?.trailingReturns?.visibility,
        writeOverride: (overrides, visibility) => ({
            ...overrides,
            performance: {
                ...overrides.performance,
                trailingReturns: { ...overrides.performance?.trailingReturns, visibility },
            },
        }),
        isOverridden: (overrides) => overrides.performance?.trailingReturns !== undefined,
        clearOverride: (overrides) => withoutPerformanceKey(overrides, 'trailingReturns'),
    },
    benchmarkComparison: {
        read: (settings) => settings.performance.benchmarkComparison,
        readOverride: (overrides) => overrides.performance?.benchmarkComparison,
        writeOverride: (overrides, visibility) => ({
            ...overrides,
            performance: { ...overrides.performance, benchmarkComparison: visibility },
        }),
        isOverridden: (overrides) => overrides.performance?.benchmarkComparison !== undefined,
        clearOverride: (overrides) => withoutPerformanceKey(overrides, 'benchmarkComparison'),
    },
    riskStatistics: {
        read: (settings) => settings.performance.riskStatistics.visibility,
        readOverride: (overrides) => overrides.performance?.riskStatistics?.visibility,
        writeOverride: (overrides, visibility) => ({
            ...overrides,
            performance: {
                ...overrides.performance,
                riskStatistics: { ...overrides.performance?.riskStatistics, visibility },
            },
        }),
        isOverridden: (overrides) => overrides.performance?.riskStatistics !== undefined,
        clearOverride: (overrides) => withoutPerformanceKey(overrides, 'riskStatistics'),
    },
    accruedBondIncome: holdingsVisibilityLens('accruedBondIncome'),
    bondAnalytics: holdingsVisibilityLens('bondAnalytics'),
    granularAssetClassBreakdown: holdingsVisibilityLens('granularAssetClassBreakdown'),
};

export const getSettingVisibility = (
    settings: ReportingSettings,
    setting: ReportingSettingId,
): ReportingChannelVisibility => VISIBILITY_LENSES[setting].read(settings);

export const setVisibilityOverride = (
    overrides: ReportingSettingsOverrides,
    setting: ReportingSettingId,
    channel: ReportingChannel,
    value: boolean,
): ReportingSettingsOverrides => {
    const lens = VISIBILITY_LENSES[setting];
    return lens.writeOverride(overrides, { ...lens.readOverride(overrides), [channel]: value });
};

export const setEnabledPeriodsOverride = (
    overrides: ReportingSettingsOverrides,
    enabledPeriods: ReportingPeriod[],
): ReportingSettingsOverrides => ({
    ...overrides,
    performance: {
        ...overrides.performance,
        trailingReturns: { ...overrides.performance?.trailingReturns, enabledPeriods },
    },
});

export const setRiskStatisticsOverride = (
    overrides: ReportingSettingsOverrides,
    statistics: RiskStatistic[],
): ReportingSettingsOverrides => ({
    ...overrides,
    performance: {
        ...overrides.performance,
        riskStatistics: { ...overrides.performance?.riskStatistics, statistics },
    },
});

export const isSettingOverridden = (overrides: ReportingSettingsOverrides, setting: ReportingSettingId): boolean =>
    VISIBILITY_LENSES[setting].isOverridden(overrides);

export const clearSettingOverride = (
    overrides: ReportingSettingsOverrides,
    setting: ReportingSettingId,
): ReportingSettingsOverrides => VISIBILITY_LENSES[setting].clearOverride(overrides);

const visibilityOverrideEqual = (
    left?: ReportingChannelVisibilityOverride,
    right?: ReportingChannelVisibilityOverride,
) =>
    left?.portalVisible === right?.portalVisible &&
    left?.reportsVisible === right?.reportsVisible &&
    left?.hubVisible === right?.hubVisible;

// An absent collection means "inherit" while a present one replaces the inherited
// value outright, so presence must be compared before content. Content compares as
// a set: the backend serializes Java Sets in arbitrary order.
const selectionEqual = <T>(left?: T[], right?: T[]) => {
    if (left === undefined || right === undefined) {
        return left === right;
    }
    return left.length === right.length && left.every((value) => right.includes(value));
};

export const reportingSettingsOverridesEqual = (left: ReportingSettingsOverrides, right: ReportingSettingsOverrides) =>
    visibilityOverrideEqual(left.advisoryFeesPaid, right.advisoryFeesPaid) &&
    visibilityOverrideEqual(
        left.performance?.trailingReturns?.visibility,
        right.performance?.trailingReturns?.visibility,
    ) &&
    selectionEqual(
        left.performance?.trailingReturns?.enabledPeriods,
        right.performance?.trailingReturns?.enabledPeriods,
    ) &&
    visibilityOverrideEqual(left.performance?.benchmarkComparison, right.performance?.benchmarkComparison) &&
    visibilityOverrideEqual(
        left.performance?.riskStatistics?.visibility,
        right.performance?.riskStatistics?.visibility,
    ) &&
    selectionEqual(left.performance?.riskStatistics?.statistics, right.performance?.riskStatistics?.statistics) &&
    visibilityOverrideEqual(left.holdings?.accruedBondIncome, right.holdings?.accruedBondIncome) &&
    visibilityOverrideEqual(left.holdings?.bondAnalytics, right.holdings?.bondAnalytics) &&
    visibilityOverrideEqual(left.holdings?.granularAssetClassBreakdown, right.holdings?.granularAssetClassBreakdown);

export const hasReportingSettingsOverrides = (overrides: ReportingSettingsOverrides) =>
    !reportingSettingsOverridesEqual(overrides, {});
